/**
 * `/quote/sequences` — Follow-up sequences + active enrollments (M-QUOTE.3.c).
 *
 * The full multi-step editor lives at `/quote/sequences/new`; per-sequence
 * detail at `/quote/sequences/:id`. This page is now strictly a list +
 * link-out.
 */
import { Link } from "react-router-dom";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Skeleton } from "@/components/ui/skeleton";
import {
  useCancelQuoteEnrollment,
  useQuoteEnrollments,
  useQuoteSequences,
} from "@/hooks/use-quote";
import { formatTimestamp } from "@/lib/formatters";

export function QuoteSequencesPage() {
  const sequences = useQuoteSequences();
  const enrollments = useQuoteEnrollments();
  const cancelEnroll = useCancelQuoteEnrollment();

  return (
    <div className="flex flex-col gap-6">
      <header className="flex flex-wrap items-start justify-between gap-3">
        <div>
          <p className="text-xs font-bold uppercase tracking-[0.2em] text-primary mb-1">Quote</p>
          <h1 className="text-2xl font-extrabold tracking-tight text-gray-900">
            Follow-up sequences
          </h1>
          <p className="text-sm leading-relaxed text-muted-foreground">
            Multi-step drips that chase stale quote opportunities. Each step produces a draft for
            your approval before it sends.
          </p>
        </div>
        <Link to="/quote/sequences/new">
          <Button data-testid="quote-seq-new-cta">+ New sequence</Button>
        </Link>
      </header>

      <section className="flex flex-col gap-3">
        <h2 className="text-lg font-semibold">Sequences</h2>
        {sequences.isLoading && <Skeleton className="h-16 w-full" />}
        {sequences.error && (
          <Card>
            <CardContent className="pt-6">
              <p className="text-sm text-destructive" data-testid="quote-seq-list-error">
                Couldn't load sequences: {sequences.error.message}
              </p>
            </CardContent>
          </Card>
        )}
        {sequences.data && sequences.data.items.length === 0 && !sequences.isLoading && (
          <Card>
            <CardContent className="pt-6">
              <p
                className="text-sm text-muted-foreground"
                data-testid="quote-seq-list-empty"
              >
                No sequences yet. Create one to start drafting follow-ups automatically.
              </p>
            </CardContent>
          </Card>
        )}
        {sequences.data && sequences.data.items.length > 0 && (
          <ul
            className="grid grid-cols-1 gap-2 md:grid-cols-2"
            data-testid="quote-seq-list"
          >
            {sequences.data.items.map((s) => (
              <li key={s.id}>
                <Link
                  to={`/quote/sequences/${s.id}`}
                  className="block rounded outline-none focus-visible:ring-2 focus-visible:ring-primary/40"
                  data-testid={`quote-seq-list-row-${s.id}`}
                >
                  <Card className="hover:bg-gray-50">
                    <CardHeader>
                      <CardTitle className="text-base">{s.name}</CardTitle>
                      <CardDescription>
                        {s.description ?? "—"} · {formatTimestamp(s.created_at)}
                      </CardDescription>
                    </CardHeader>
                  </Card>
                </Link>
              </li>
            ))}
          </ul>
        )}
      </section>

      <section className="flex flex-col gap-3">
        <h2 className="text-lg font-semibold">Active enrollments</h2>
        {enrollments.isLoading && <Skeleton className="h-16 w-full" />}
        {enrollments.data && enrollments.data.items.length === 0 && !enrollments.isLoading && (
          <p className="text-sm text-muted-foreground">
            No active enrollments. Enroll a lead via the API (
            <code className="rounded bg-muted px-1 text-xs">POST /api/v1/quote/enrollments</code>)
            or from the lead detail page (coming soon).
          </p>
        )}
        {enrollments.data && enrollments.data.items.length > 0 && (
          <ul className="grid grid-cols-1 gap-2">
            {enrollments.data.items.map((e) => (
              <li key={e.id}>
                <Card>
                  <CardContent className="flex items-center justify-between gap-3 pt-6">
                    <div className="min-w-0 text-sm">
                      <div className="font-mono text-xs">{e.id}</div>
                      <div className="text-muted-foreground">
                        Lead {e.lead_id.slice(0, 8)}… on sequence {e.sequence_id.slice(0, 8)}… ·
                        step {e.current_step_order}
                      </div>
                    </div>
                    <div className="flex items-center gap-2">
                      <Badge variant={e.status === "active" ? "default" : "outline"}>
                        {e.status}
                      </Badge>
                      {e.status === "active" && (
                        <Button
                          variant="outline"
                          size="sm"
                          onClick={() => cancelEnroll.mutate(e.id)}
                          disabled={cancelEnroll.isPending}
                        >
                          Cancel
                        </Button>
                      )}
                    </div>
                  </CardContent>
                </Card>
              </li>
            ))}
          </ul>
        )}
      </section>
    </div>
  );
}
